import type { ClientMessage, ServerMessage } from "@mmo/shared";

export type NetHandlers = {
  onWelcome: (id: string, tickRate: number) => void;
  onSnapshot: (msg: Extract<ServerMessage, { t: "snapshot" }>) => void;
  onShot: (msg: Extract<ServerMessage, { t: "shot" }>) => void;
  onHit: (msg: Extract<ServerMessage, { t: "hit" }>) => void;
  onDeath: (msg: Extract<ServerMessage, { t: "death" }>) => void;
  onStatus: (status: "connecting" | "open" | "closed") => void;
};

export class Net {
  private ws: WebSocket | null = null;
  private url: string;
  private handlers: NetHandlers;
  private reconnectDelay = 1000;

  constructor(url: string, handlers: NetHandlers) {
    this.url = url;
    this.handlers = handlers;
  }

  connect(name: string) {
    this.handlers.onStatus("connecting");
    const ws = new WebSocket(this.url);
    this.ws = ws;
    ws.onopen = () => {
      this.handlers.onStatus("open");
      this.reconnectDelay = 1000;
      this.send({ t: "hello", name });
    };
    ws.onmessage = (ev) => {
      let msg: ServerMessage;
      try { msg = JSON.parse(ev.data); } catch { return; }
      if (msg.t === "welcome") this.handlers.onWelcome(msg.id, msg.tickRate);
      else if (msg.t === "snapshot") this.handlers.onSnapshot(msg);
      else if (msg.t === "shot") this.handlers.onShot(msg);
      else if (msg.t === "hit") this.handlers.onHit(msg);
      else if (msg.t === "death") this.handlers.onDeath(msg);
    };
    ws.onclose = () => {
      this.handlers.onStatus("closed");
      this.ws = null;
      setTimeout(() => this.connect(name), this.reconnectDelay);
      this.reconnectDelay = Math.min(this.reconnectDelay * 2, 8000);
    };
    ws.onerror = () => { try { ws.close(); } catch {} };
  }

  send(msg: ClientMessage) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }
}

export function defaultServerUrl(): string {
  const env = (import.meta as any).env?.VITE_SERVER_URL as string | undefined;
  if (env) return env;
  const proto = location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${location.hostname}:8080`;
}
